<script setup lang="ts">
  import NewUserSheet from '@/views/users/components/NewUserSheet.vue'
  import Table from '@/components/table/Table.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useUserStore } from '@/store/user.store'
  import type { NestUser } from '@/types/user'
  import {
    ConfirmationDialog,
    require,
    UiButton,
    useToastStore,
    type Column,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'

  const userStore = useUserStore()
  const router = useRouter()
  const toastStore = useToastStore()

  const columns: Column[] = [
    { key: 'email', label: 'Email', searchable: true },
    { key: 'firstName', label: 'Voornaam', searchable: true },
    { key: 'lastName', label: 'Achternaam', searchable: true },
    { key: 'role', label: 'Rol', searchable: true },
    { key: 'actions', label: 'Acties', sticky: 'right' },
  ]

  const users = ref<NestUser[]>([])
  const showNewUserSheet = ref<boolean>(false)

  const errorMessage = ref<string | null>()
  const deletingUser = ref<NestUser | null>(null)

  const showConfirmationDialog = computed({
    get: () => deletingUser.value != null,
    set: value => {
      if (!value) deletingUser.value = null
    },
  })

  const roleMap = {
    admin: 'Admin',
    user: 'Gebruiker',
    viewer: 'Kijkgebruiker',
    owner: 'Eigenaar',
  } as const

  onMounted(async () => {
    await userStore.fetchMe()
    await fetchUsers()
  })

  async function fetchUsers() {
    try {
      users.value = await userStore.fetchAll(
        require(userStore.currentNestUser, 'Current nest user is missing from user store. Ensure fetchMe is called')
          .customerId
      )
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        errorMessage.value = error.response.data.message
      } else {
        throw error
      }
    }
  }

  async function deleteUser() {
    try {
      await userStore.deleteUser(
        require(deletingUser.value, 'User cannot be null when deleting a user').id
      )
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        toastStore.error('Je hebt niet de juiste rol om deze gebruiker te verwijderen')
      }
    }
    await fetchUsers()
    deletingUser.value = null
    toastStore.success('Gebruiker succesvol verwijderd')
  }
</script>

<template>
  <TopBar>
    <UiButton
      class="create-button"
      variant="prominent"
      icon="plus"
      @click="showNewUserSheet = true"
    />
  </TopBar>

  <ConfirmationDialog
    v-model:show="showConfirmationDialog"
    :confirm="deleteUser"
    :title="`Weet je zeker dat je ${deletingUser?.firstName} ${deletingUser?.lastName} wilt verwijderen?`"
    body="Deze actie kan niet ongedaan worden gemaakt."
    class="confirm-delete-user"
  />

  <NewUserSheet v-model:show="showNewUserSheet" @created="fetchUsers" />

  <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

  <div class="layout" v-else>
    <Table :rows="users" :columns="columns" row-key="id">
      <template #cell-role="{ row }">
        <span>{{ roleMap[row.role] }}</span>
      </template>

      <template #cell-actions="{ row }">
        <div class="button-group">
          <UiButton variant="link" icon="edit" @click="router.push(`/users/${row.id}`)" />

          <UiButton
            v-if="row.id !== userStore.currentNestUser?.id"
            variant="link"
            icon="delete"
            @click="deletingUser = row"
          />
        </div>
      </template>
    </Table>
  </div>
</template>

<style lang="css" scoped>
  .modal-overlay.confirm-delete-user {
    z-index: 20;
  }

  .error-message {
    color: var(--colours-support-negative);
  }

  .create-button {
    align-self: end;
  }

  .button-group {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-xs);
  }

  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-s);
    min-height: calc(100dvh - var(--top-bar-height));
  }
</style>
